import React, { useMemo } from "react";
import { inject, observer } from "mobx-react";
import BroadcastStore from "../_stores/broadcast/BroadcastStore";
import NodeChatStore from "../_stores/nodechat/NodeChatStore";
import CountUpTimer from "../../../../src/library/src/components/count-up-timer";
import Avatar from "library/components/avatar";
import TailwindFlex from "library/components/_tailwind/flex";
import TailwindBox from "library/components/_tailwind/box";
import TailwindText from "library/components/_tailwind/text";

const MAX_VISIBLE_AVATARS = 2;

interface MobileBroadcastTopBarProps {
  broadcastStore?: BroadcastStore;
  nodeChatStore?: NodeChatStore;
}

const MobileBroadcastTopBar: React.FC<MobileBroadcastTopBarProps> = ({
  broadcastStore,
  nodeChatStore,
}) => {
  const { isShowStarted, currentShowType, isCountUpTimerOn } = broadcastStore!;
  const {
    memberCount,
    members,
    sessionEarnings,
    sessionPayoutPercent,
    isEstimatedEarningsEnabled,
  } = nodeChatStore!;

  const earnings = useMemo(() => {
    if (
      isEstimatedEarningsEnabled &&
      sessionPayoutPercent &&
      sessionEarnings &&
      sessionEarnings > 0
    ) {
      return `$${sessionEarnings.toFixed(2)}`;
    }
    return null;
  }, [sessionEarnings, sessionPayoutPercent, isEstimatedEarningsEnabled]);

  const visibleMembers = useMemo(() => {
    return members.slice(0, MAX_VISIBLE_AVATARS);
  }, [members]);

  return (
    <TailwindFlex
      position='absolute'
      inset={["top-0", "left-0", "right-0"]}
      alignItems='items-center'
      justifyContent='justify-between'
      padding={["px-3", "py-2"]}
      zIndex='z-10'>
      {/* Left side - Timer + Earnings */}
      <TailwindFlex width='w-auto' alignItems='items-center' gap='gap-2'>
        {isShowStarted && isCountUpTimerOn && (
          <TailwindFlex
            width='w-auto'
            alignItems='items-center'
            gap='gap-1'
            padding={["px-2.5", "py-1"]}
            borderRadius='rounded-full'
            style={{ background: "rgba(0, 0, 0, 0.5)" }}
            textColor='text-white'>
            <CountUpTimer
              currentShowType={currentShowType}
              fontSize='text-xs'
              isCountUpTimerOn={isCountUpTimerOn}
            />
          </TailwindFlex>
        )}
        {earnings && (
          <TailwindBox
            padding={["px-2.5", "py-1"]}
            borderRadius='rounded-full'
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}>
            <TailwindText
              fontSize='text-xs'
              fontWeight='font-bold'
              textColor='text-white'>
              {earnings}
            </TailwindText>
          </TailwindBox>
        )}
      </TailwindFlex>

      {/* Right side - Viewer avatars + count */}
      {isShowStarted && (
        <TailwindFlex
          width='w-auto'
          alignItems='items-center'
          gap='gap-1'
          padding={["pl-1", "pr-2.5", "py-1"]}
          borderRadius='rounded-full'
          style={{
            background: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
          }}>
          {/* Overlapping avatars using library Avatar component */}
          <TailwindFlex
            width='w-auto'
            alignItems='items-center'
            style={{ marginRight: visibleMembers.length > 0 ? 4 : 0 }}>
            {visibleMembers.map((member, index) => (
              <TailwindBox
                key={member.id || member.handle || index}
                style={{
                  marginLeft: index > 0 ? -8 : 0,
                  zIndex: MAX_VISIBLE_AVATARS - index,
                  position: "relative",
                }}>
                <Avatar
                  username={member.username || member.handle || ""}
                  size='2extra-small'
                  shapeVariant='circle'
                  borderSize='thin'
                  borderColor='none'
                  randomBackgroundColor
                  fontSize='text-xs'
                />
              </TailwindBox>
            ))}
          </TailwindFlex>
          {/* Count */}
          <TailwindText
            fontSize='text-xs'
            fontWeight='font-semibold'
            textColor='text-white'>
            {memberCount}
          </TailwindText>
        </TailwindFlex>
      )}
    </TailwindFlex>
  );
};

export default inject(
  "broadcastStore",
  "nodeChatStore"
)(observer(MobileBroadcastTopBar));
